import axios, { AxiosError, AxiosInstance, AxiosResponse } from "axios";

import { environment } from "@constants/environment";
import { API_HEADERS } from "@constants/storage.constants";
import {
  getAuthTokenFromStorage,
  getGroupKeyFromStorage,
  getApplicationName
} from "@utils/storage";

/**
 * Detect if an axios error is a CORS / network error (no response from server).
 */
export function isNetworkOrCorsError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const axErr = error as AxiosError;
  return (
    !axErr.response &&
    (axErr.code === "ERR_NETWORK" ||
      axErr.code === "ECONNABORTED" ||
      axErr.message === "Network Error")
  );
}

// ── Auth error event bus ────────────────────────────────────────
// Allows any API client to signal a 401 so that providers higher in
// the tree (FlowSessionProvider) can react without tight coupling.

type AuthErrorListener = () => void;
const authErrorListeners = new Set<AuthErrorListener>();

/** Subscribe to 401/auth-error events. Returns unsubscribe function. */
export function onAuthError(listener: AuthErrorListener): () => void {
  authErrorListeners.add(listener);
  return () => {
    authErrorListeners.delete(listener);
  };
}

/** Broadcast a 401 to all registered listeners (de-duplicated per tick). */
let authErrorQueued = false;
function notifyAuthError(): void {
  if (authErrorQueued) return;
  authErrorQueued = true;
  queueMicrotask(() => {
    authErrorQueued = false;
    authErrorListeners.forEach((fn) => fn());
  });
}

/**
 * API Client Factory
 * Creates typed API client instances for different backends
 * Mirrors sage-network's APIClient pattern with group-key/token concept
 */

interface APIClientConfig {
  baseURL: string;
  includeGroupKey?: boolean;
  /** Send app-code header (required for inbox V3 middleware) */
  appCode?: string;
  /** Override default Content-Type / Accept (e.g. application/vnd.api+json for connectors) */
  contentType?: string;
  /** Attach a unique Idempotency-Key header to every POST request */
  includeIdempotencyKey?: boolean;
}

function createAPIClient({
  baseURL,
  includeGroupKey = false,
  contentType,
  includeIdempotencyKey = false
}: APIClientConfig) {
  const instance: AxiosInstance = axios.create({
    baseURL,
    headers: {
      "X-Requested-With": "XMLHttpRequest",
      Pragma: "no-cache",
      Expires: "-1"
    }
  });

  // Request interceptor - attach auth token and group key
  instance.interceptors.request.use(
    async (config) => {
      const token = getAuthTokenFromStorage();

      if (token) {
        config.headers[API_HEADERS.AUTHORIZATION] = `Bearer ${token}`;
      }

      const defaultContentType = contentType ?? "application/json";
      const defaultAccept = contentType ?? "text/plain";
      config.headers[API_HEADERS.CONTENT_TYPE] =
        config.headers[API_HEADERS.CONTENT_TYPE] || defaultContentType;
      config.headers.Accept = config.headers.Accept || defaultAccept;
      config.headers[API_HEADERS.APPLICATION_NAME] = getApplicationName();

      if (includeIdempotencyKey && config.method === "post") {
        config.headers["Idempotency-Key"] = crypto.randomUUID();
      }

      if (includeGroupKey) {
        const datasetId = getGroupKeyFromStorage();
        if (datasetId && !config.headers[API_HEADERS.X_DATASET_ID]) {
          config.headers[API_HEADERS.X_DATASET_ID] = datasetId;
        }
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - handle common errors
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error) => {
      if (error.response?.status === 401) {
        notifyAuthError();
      }
      return Promise.reject(error);
    }
  );

  return instance;
}

/**
 * Onboarding (Bootstrap) API Client
 * Base: REACT_APP_E_INVOICING_API_ENDPOINT + /api/v1
 * Used for: accesspoints, datasets/{id}/accesspoints, connections
 * Adds a second response interceptor that transforms JSON:API error envelopes
 * into plain Error objects with .status / .title / .detail / .traceId fields.
 */
export const eInvoicingBFFClient = (() => {
  const instance = createAPIClient({
    baseURL: `${environment.REACT_APP_E_INVOICING_API_ENDPOINT}v1`,
    includeIdempotencyKey: true,
    includeGroupKey: true
  });

  // JSON:API error transformer — runs after the factory's 401 interceptor.
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const jsonApiErrors = error?.response?.data?.errors;
      if (Array.isArray(jsonApiErrors) && jsonApiErrors.length > 0) {
        const first = jsonApiErrors[0] as {
          status?: number;
          title?: string;
          detail?: string;
          source?: unknown;
        };
        const transformed = new Error(
          first.title ?? "Unknown error"
        ) as Error & {
          status: number;
          title: string;
          detail: string;
          source: unknown;
          traceId: string | null;
        };
        transformed.status =
          first.status ?? (error.response?.status as number) ?? 0;
        transformed.title = first.title ?? "";
        transformed.detail = first.detail ?? "";
        transformed.source = (first as { source?: unknown }).source ?? null;
        transformed.traceId =
          (error.response?.headers?.["tracestate"] as string | undefined) ??
          (error.response?.headers?.["traceparent"] as string | undefined) ??
          null;
        return Promise.reject(transformed);
      }
      return Promise.reject(error);
    }
  );

  return instance;
})();
